import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
	Layout,
	Spin,
	Typography,
	Card,
	Row,
	Col,
	Tabs,
	message,
	Form,
	Input,
	Button,
	Space,
	Timeline,
	Divider,
	Radio,
	RadioChangeEvent,
} from 'antd';
import { useMutation } from '@tanstack/react-query';
import { fetchDetailedCallInformation, getCallRecording } from '@/api/logs';
import { CallLog } from '@/interfaces/ILogs';
import './CallInformation.scss';
import { CallInformationCard } from './CallInformationCard';
import { CallTranscriptCard } from './CallTranscriptCard';
import { CallInakeFormCard } from './CallIntakeFormCard';
import { IntakeForm, TranscriptMessage } from '@/types/TCallLogs';
import { CallSummaryCard } from './CallSummaryCard';
import {
	DownOutlined,
	UpOutlined,
	LinkOutlined,
	PhoneOutlined,
	CalendarOutlined,
} from '@ant-design/icons';
import { RequestData } from '@/interfaces/ICallInformation';

const { Content } = Layout;
const { Title, Text } = Typography;

export const CallInformationDetailed = () => {
	const { key } = useParams();
	const [callInformation, setCallInformation] = useState<CallLog | null>(null);
	const [audioURl, setAudioURL] = useState<string | null>(null);
	const [intakeForm, setIntakeForm] = useState<IntakeForm[]>([]);
	const [audioLoading, setAudioLoading] = useState<boolean>(false);
	const [showSearchForm, setShowSearchForm] = useState<boolean>(true);
	const [queryType, setQueryType] = useState<string>('callSid');
	const [form] = Form.useForm();
	const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);

	const {
		mutate: getCallInformation,
		isPending,
		isSuccess,
	} = useMutation({
		mutationKey: ['DetailedCallInformation'],
		mutationFn: fetchDetailedCallInformation,
		onSuccess: (data) => {
			setCallInformation(data?.data);
			parseIntakeResponse(data?.data?.intakeResponses as string);
			setShowSearchForm(false); // Hide form after successful response
		},
		onError: (error) => {
			console.log(error.message);
			message.error(error.message || 'Failed to fetch call information');
		},
	});

	const { mutate: callRecordingMutation } = useMutation({
		mutationKey: ['getCallRecording'],
		mutationFn: getCallRecording,
		onSuccess: (data) => {
			setAudioURL(data.url);
			setAudioLoading(false);
		},
		onError: () => {
			setAudioLoading(false);
			message.error('Failed to fetch call recording');
		},
	});

	// useEffect(() => {
	//   if (key) {
	//     // If we have a key in the URL params, use it as a shareable link
	//     const shareableLink = window.decodeURIComponent(key);
	//     getCallInformation({ shareableLink });
	//   }
	// }, [key]);

	useEffect(() => {
		if (callInformation?.isRecorded && callInformation.callSid) {
			setAudioLoading(true);
			callRecordingMutation(callInformation.callSid);
		}
	}, [callInformation?.callSid]);

	const handleSearch = (values: RequestData) => {
		const { callSid, forwardedFrom, date, shareableLink } = values;

		// Based on the query type, validate and send the appropriate parameters
		if (queryType === 'shareableLink' && shareableLink) {
			let link;
			try {
				console.log(shareableLink.split('/call-information/'));
				link = window.decodeURIComponent(shareableLink.split('/call-information/')[1]);
			} catch {
				message.error('Invalid shareable link');
			}
			getCallInformation({ shareableLink: link });
		} else if (queryType === 'callSid' && callSid) {
			getCallInformation({ callSid });
		} else if (queryType === 'dateAndUser' && forwardedFrom && date) {
			getCallInformation({ forwardedFrom, date });
		} else {
			message.error('Please fill in all required fields for the selected query type');
		}
	};

	const toggleSearchForm = () => {
		setShowSearchForm(!showSearchForm);
	};

	const handleQueryTypeChange = (e: RadioChangeEvent) => {
		setQueryType(e.target.value);
		form.resetFields(['callSid', 'forwardedFrom', 'date', 'shareableLink']);
	};

	const handlePlaybackSpeedChange = (value: number) => {
		setPlaybackSpeed(value);
	};

	const transcriptMessages: TranscriptMessage[] = callInformation?.transcript
		? (callInformation.transcript
				.split('\n')
				.map((message: string, index: number) => {
					if (message.startsWith('assistant@')) {
						return { key: index, sender: 'Assistant', message: message.replace('assistant@', '') };
					} else if (message.startsWith('user@')) {
						return { key: index, sender: 'User', message: message.replace('user@', '') };
					}
					return null;
				})
				.filter(Boolean) as TranscriptMessage[])
		: [];

	const parseIntakeResponse = (intakeResponse: string) => {
		try {
			if (!intakeResponse) {
				return [];
			}
			const parsedResponse = JSON.parse(intakeResponse);
			if (Array.isArray(parsedResponse)) {
				const transformedResponse = parsedResponse
					.map((obj) => {
						return Object.entries(obj)
							.map(([key, value]) => [
								{ key: 'Question', value: key },
								{ key: 'Answer', value },
							])
							.flat();
					})
					.flat() as IntakeForm[];
				setIntakeForm(transformedResponse);
			} else if (typeof parsedResponse === 'object' && parsedResponse !== null) {
				const transformedResponse = Object.entries(parsedResponse)
					.map(([key, value]) => [
						{ key: 'Question', value: key },
						{ key: 'Answer', value },
					])
					.flat() as IntakeForm[];

				setIntakeForm(transformedResponse);
			}
		} catch {
			message.error('Error parsing intake response');
			return [];
		}
	};

	// Format the meta logs date according to user's timezone
	const formatMetaLogDate = (timestamp: number) => {
		if (!timestamp) {
			return '';
		}

		try {
			const date = new Date(timestamp);
			const adjustedDate = new Date(date.getTime() + 5 * 60 * 60 * 1000);
			return adjustedDate.toLocaleString(undefined);
		} catch (error) {
			console.error('Error formatting date:', error);
			return new Date(timestamp).toLocaleString();
		}
	};

	const MetaLogsCard = ({ metaLogs, style }: { metaLogs: any; style?: React.CSSProperties }) => {
		if (!metaLogs || !metaLogs.logs || metaLogs.logs.length === 0) {
			return <Card style={style}>No meta logs available</Card>;
		}

		// Get log level color and icon
		const getLogLevelProps = (level: string) => {
			switch (level.toLowerCase()) {
				case 'error':
					return {
						color: 'red',
						dotColor: '#ff4d4f',
						textType: 'danger',
						bgColor: '#fff1f0',
						borderColor: '#ffccc7',
						textColor: '#cf1322',
					};
				case 'warning':
					return {
						color: 'orange',
						dotColor: '#faad14',
						textType: 'warning',
						bgColor: '#fffbe6',
						borderColor: '#ffe58f',
						textColor: '#d4b106',
					};
				case 'info':
					return {
						color: 'blue',
						dotColor: '#1890ff',
						textType: undefined,
						bgColor: '#e6f7ff',
						borderColor: '#91caff',
						textColor: '#1890ff',
					};
				default:
					return {
						color: 'blue',
						dotColor: '#1890ff',
						textType: undefined,
						bgColor: '#e6f7ff',
						borderColor: '#91caff',
						textColor: '#1890ff',
					};
			}
		};

		// Format log data properly
		const formatLogData = (data: any) => {
			if (Array.isArray(data)) {
				return data.map((item, index) => {
					if (typeof item === 'object' && item !== null) {
						try {
							return (
								<pre key={index} style={{ margin: '4px 0', overflow: 'auto', maxHeight: '300px' }}>
									{JSON.stringify(item, null, 2)}
								</pre>
							);
						} catch {
							return String(item);
						}
					}
					return String(item);
				});
			} else if (typeof data === 'object' && data !== null) {
				try {
					return (
						<pre style={{ margin: '4px 0', overflow: 'auto', maxHeight: '300px' }}>
							{JSON.stringify(data, null, 2)}
						</pre>
					);
				} catch {
					return String(data);
				}
			}
			return String(data);
		};

		return (
			<Card style={style}>
				<style>
					{`
          .log-content pre {
            font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
            background-color: #f5f5f5;
            border-radius: 4px;
            padding: 8px;
            white-space: pre-wrap;
            word-break: break-word;
          }
          .log-item {
            margin-bottom: 16px;
          }
          .log-header {
            margin-bottom: 4px;
          }
          `}
				</style>
				<Timeline
					items={metaLogs.logs.map((log: any, index: number) => {
						const { color, bgColor, borderColor, textColor } = getLogLevelProps(log.level);

						return {
							key: index,
							color,
							children: (
								<div className="log-item">
									<div className="log-header">
										<Text strong>{formatMetaLogDate(log.date)}</Text>
									</div>
									<div className={`log-content log-${log.level.toLowerCase()}`}>
										<Text
											style={{
												display: 'inline-block',
												padding: '2px 8px',
												borderRadius: '4px',
												backgroundColor: bgColor,
												border: `1px solid ${borderColor}`,
												marginRight: '8px',
												color: textColor,
												fontWeight: 'bold',
												fontFamily: 'Consolas, Monaco, Courier New, monospace',
											}}
										>
											{log.level.toUpperCase()}
										</Text>
										<div
											style={{
												marginTop: '4px',
												color: textColor,
												fontFamily: 'Consolas, Monaco, Courier New, monospace',
											}}
										>
											{formatLogData(log.data)}
										</div>
									</div>
								</div>
							),
						};
					})}
				/>
			</Card>
		);
	};

	const tabList = [
		{
			key: '1',
			label: 'Call Summary',
			children: (
				<CallSummaryCard
					callSummary={callInformation?.callSummary}
					style={{ minHeight: '72vh', maxHeight: '72vh', overflowY: 'auto' }}
				/>
			),
		},
		{
			key: '2',
			label: 'Transcript',
			children: (
				<CallTranscriptCard
					transcriptMessages={transcriptMessages}
					style={{ minHeight: '72vh', maxHeight: '72vh', overflowY: 'auto' }}
				/>
			),
		},
		{
			key: '3',
			label: 'Intake Form',
			children: (
				<CallInakeFormCard
					intakeForm={intakeForm}
					style={{ minHeight: '72vh', maxHeight: '72vh', overflowY: 'auto' }}
				/>
			),
		},
		{
			key: '4',
			label: 'Meta Logs',
			children: (
				<MetaLogsCard
					metaLogs={callInformation?.metaLogs}
					style={{ minHeight: '72vh', maxHeight: '72vh', overflowY: 'auto' }}
				/>
			),
		},
	];

	const baseClass = 'call-information';

	return (
		<Layout className={baseClass}>
			<Content>
				<div className={`${baseClass}__content`}>
					<div className={`${baseClass}__content__header`}>
						<Title level={3}>Detailed Call Information</Title>
					</div>

					{isSuccess && callInformation && !showSearchForm ? (
						<Button type="link" icon={<DownOutlined />} onClick={toggleSearchForm} className="mb-4">
							Show Search Form
						</Button>
					) : null}

					{!key && showSearchForm && (
						<Card className="mb-4">
							<Form form={form} layout="vertical" onFinish={handleSearch}>
								<Form.Item label="Search Method">
									<Radio.Group onChange={handleQueryTypeChange} value={queryType}>
										<Radio.Button value="callSid">By Call SID</Radio.Button>
										<Radio.Button value="dateAndUser">By User & Date</Radio.Button>
										<Radio.Button value="shareableLink">By Shareable Link</Radio.Button>
									</Radio.Group>
								</Form.Item>

								<Divider />

								{queryType === 'callSid' && (
									<Row gutter={16}>
										<Col span={24}>
											<Form.Item
												name="callSid"
												label="Call SID"
												rules={[{ required: true, message: 'Call SID is required' }]}
											>
												<Input
													prefix={<PhoneOutlined />}
													placeholder="Enter call SID (e.g. CAXXXX)"
												/>
											</Form.Item>
										</Col>
									</Row>
								)}

								{queryType === 'dateAndUser' && (
									<Row gutter={16}>
										<Col xs={24} sm={24} md={12}>
											<Form.Item
												name="forwardedFrom"
												label="Forwarded From"
												rules={[{ required: true, message: 'Forwarded From is required' }]}
											>
												<Input
													prefix={<PhoneOutlined />}
													placeholder="Enter forwardedFrom (e.g. johndoe)"
												/>
											</Form.Item>
										</Col>
										<Col xs={24} sm={24} md={12}>
											<Form.Item
												name="date"
												label="Date"
												rules={[{ required: true, message: 'Date is required' }]}
											>
												<Input
													prefix={<CalendarOutlined />}
													placeholder="Enter date (e.g. 3/9/2025, 4:00:52 AM)"
												/>
											</Form.Item>
										</Col>
									</Row>
								)}

								{queryType === 'shareableLink' && (
									<Row gutter={16}>
										<Col span={24}>
											<Form.Item
												name="shareableLink"
												label="Shareable Link"
												rules={[{ required: true, message: 'Shareable link is required' }]}
											>
												<Input prefix={<LinkOutlined />} placeholder="Enter the shareable link" />
											</Form.Item>
										</Col>
									</Row>
								)}

								<Form.Item>
									<Space>
										<Button type="primary" htmlType="submit" loading={isPending}>
											Search
										</Button>
										<Button
											onClick={() => {
												form.resetFields();
												setQueryType('callSid');
											}}
										>
											Reset
										</Button>
										{isSuccess && callInformation && (
											<Button icon={<UpOutlined />} onClick={toggleSearchForm}>
												Hide Search Form
											</Button>
										)}
									</Space>
								</Form.Item>
							</Form>
						</Card>
					)}

					{isPending ? (
						<div className="text-center">
							<Spin />
						</div>
					) : isSuccess && callInformation ? (
						<div className={`${baseClass}__content__body`}>
							<Row gutter={[16, 16]}>
								<Col xs={24} lg={12}>
									<CallInformationCard
										callInformation={callInformation as CallLog}
										audioURl={audioURl}
										audioLoading={audioLoading}
										playbackSpeed={playbackSpeed || 1}
										onPlaybackSpeedChange={handlePlaybackSpeedChange}
									/>
								</Col>
								<Col xs={24} lg={12}>
									{callInformation?.intakeResponses ||
									callInformation?.callSummary ||
									callInformation?.transcript ||
									callInformation?.metaLogs ? (
										<Tabs
											defaultActiveKey={
												callInformation?.callSummary ? '1' : callInformation?.transcript ? '2' : '4'
											}
											size="small"
											type="card"
											centered
											items={tabList}
										/>
									) : (
										<Card className="text-center">
											<Title level={4}>No data available</Title>
										</Card>
									)}
								</Col>
							</Row>
						</div>
					) : key ? (
						<Col xs={24} className="p-10">
							<Card className="text-center">
								<Title level={4}>No call information found</Title>
							</Card>
						</Col>
					) : null}
				</div>
			</Content>
		</Layout>
	);
};
